import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";

import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { useSession } from "@/hooks/use-session";
import {
  getRealKeywordSuggestions,
  type RealSuggestion,
  type SuggestGroup,
} from "@/lib/keyword-suggest.functions";

const COUNTRIES: { code: "in" | "us" | "gb" | "ae" | "au" | "ca"; label: string }[] = [
  { code: "in", label: "India" },
  { code: "us", label: "United States" },
  { code: "gb", label: "United Kingdom" },
  { code: "ae", label: "UAE" },
  { code: "au", label: "Australia" },
  { code: "ca", label: "Canada" },
];

const GROUP_STYLES: Record<SuggestGroup, string> = {
  Questions: "bg-sky-50 text-sky-700",
  Comparisons: "bg-amber-50 text-amber-700",
  Commercial: "bg-emerald-50 text-emerald-700",
  Related: "bg-violet-50 text-violet-700",
};

const INFORMATIONAL = ["how to", "what is", "why", "guide to", "examples of", "checklist for", "best practices for"];
const COMMERCIAL = ["best", "top", "cheap", "affordable", "compare", "alternatives to", "review of"];
const TRANSACTIONAL = ["hire", "buy", "pricing for", "cost of", "agency for", "services", "consultant for"];
const LOCAL = ["in india", "in delhi", "near me", "for startups", "for saas", "for b2b"];
const QUESTIONS = [
  "What is {k}?",
  "How does {k} work?",
  "Why is {k} important in 2026?",
  "How much does {k} cost?",
  "Who needs {k}?",
  "How do I get started with {k}?",
];

type Intent = "Informational" | "Commercial" | "Transactional" | "Local / niche";

interface Idea {
  keyword: string;
  intent: Intent;
  difficulty: number;
  opportunity: "High" | "Medium" | "Low";
  type: string;
}

// Competition estimate from observable phrase structure only — no random or
// invented values. Longer, more specific, question-led and locally qualified
// phrases face fewer competing pages; short head terms face the most.
function estimateCompetition(keyword: string, intent: Intent, type: string) {
  const words = keyword.split(" ").filter(Boolean).length;
  let score = 85;
  score -= Math.max(0, words - 1) * 9; // each extra qualifier narrows the SERP
  if (/^(what|how|why)\b/.test(keyword)) score -= 8; // question phrasing
  if (intent === "Local / niche") score -= 8; // audience/geo qualifier
  if (type === "Money term") score += 6; // commercial pages bid harder
  return Math.max(10, Math.min(90, Math.round(score)));
}

function buildIdeas(seed: string): Idea[] {
  const k = seed.trim().toLowerCase().replace(/\s+/g, " ");
  const make = (keyword: string, intent: Intent, type: string): Idea => {
    const difficulty = estimateCompetition(keyword, intent, type);
    const opportunity = difficulty < 40 ? "High" : difficulty < 60 ? "Medium" : "Low";
    return { keyword, intent, difficulty, opportunity, type };
  };

  return [
    make(k, "Informational", "Head term"),
    ...INFORMATIONAL.map((m) => make(`${m} ${k}`, "Informational", "Long-tail")),
    ...COMMERCIAL.map((m) => make(`${m} ${k}`, "Commercial", "Comparison")),
    ...TRANSACTIONAL.map((m) => make(m.endsWith("for") || m === "hire" || m === "buy" ? `${m} ${k}` : `${k} ${m}`, "Transactional", "Money term")),
    ...LOCAL.map((m) => make(`${k} ${m}`, "Local / niche", "Modifier")),
  ];
}

function semanticTerms(seed: string) {
  const k = seed.trim().toLowerCase();
  return [
    `${k} strategy`, `${k} tools`, `${k} metrics`, `${k} examples`, `${k} benefits`,
    `${k} mistakes`, `${k} checklist`, `${k} for beginners`, `${k} vs alternatives`,
    `${k} roi`, `${k} workflow`, `${k} trends 2026`,
  ];
}

const INTENT_STYLES: Record<Intent, string> = {
  Informational: "bg-sky-50 text-sky-700",
  Commercial: "bg-amber-50 text-amber-700",
  Transactional: "bg-emerald-50 text-emerald-700",
  "Local / niche": "bg-violet-50 text-violet-700",
};

const FAQS = [
  {
    q: "How does this keyword explorer work?",
    a: "It expands your seed keyword into long-tail, commercial, transactional, and niche variations, groups them by search intent, and estimates how competitive each one looks based on phrase length and specificity. Everything runs in your browser — nothing is stored.",
  },
  {
    q: "Are the difficulty scores real search data?",
    a: "No. They are directional estimates based on keyword structure, not live Google data. Use them to prioritise which variations to research further in a paid tool like Google Keyword Planner or Semrush.",
  },
  {
    q: "Which keywords should I target first?",
    a: "Start with the long-tail variations marked High opportunity. They are specific, easier to rank for, and usually convert better than broad head terms.",
  },
  {
    q: "How many keywords should one page target?",
    a: "One primary keyword per page, supported by five to ten semantic terms. Splitting a page across several primary keywords usually weakens all of them.",
  },
  {
    q: "Can you do the keyword research for us?",
    a: "Yes. Our team builds full keyword maps, briefs, and publishing calendars — see our [services](/services) or check our [pricing plans](/pricing).",
  },
];

export const Route = createFileRoute("/tools/keyword-explorer")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/tools/keyword-explorer" }],
    meta: [
      { property: "og:url", content: "https://mgcontentlabs.com/tools/keyword-explorer" },
      { title: "Free Long Tail Keyword Generator Tool (India) | MG Content Labs" },
      {
        name: "description",
        content:
          "Free long tail keyword generator: turn one seed keyword into question keywords and semantic terms grouped by search intent, with live Google suggestions. No signup.",
      },
      { property: "og:title", content: "Free Long Tail Keyword Generator Tool" },
      {
        property: "og:description",
        content:
          "Turn one seed keyword into long-tail ideas, question keywords and semantic terms grouped by search intent. Free and instant.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "MG Content Labs Keyword Explorer",
          url: "https://mgcontentlabs.com/tools/keyword-explorer",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          creator: { "@type": "Organization", name: "MG Content Labs" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a.replace(/\[(.+?)\]\(.+?\)/g, "$1") },
          })),
        }),
      },
    ],
  }),
  component: KeywordExplorer,
});

function KeywordExplorer() {
  // Pre-filled sample so visitors and search crawlers see real results on first load.
  const [seed, setSeed] = useState("content marketing agency");
  const [filter, setFilter] = useState<"All" | Intent>("All");
  const [copied, setCopied] = useState(false);

  const { user, loading: sessionLoading } = useSession();
  const fetchReal = useServerFn(getRealKeywordSuggestions);
  const [country, setCountry] = useState<(typeof COUNTRIES)[number]["code"]>("in");
  const [real, setReal] = useState<RealSuggestion[] | null>(null);
  const [realLoading, setRealLoading] = useState(false);
  const [realError, setRealError] = useState<string | null>(null);


  const trimmed = seed.trim();
  const ideas = useMemo(() => (trimmed ? buildIdeas(trimmed) : []), [trimmed]);
  const terms = useMemo(() => (trimmed ? semanticTerms(trimmed) : []), [trimmed]);
  const questions = useMemo(
    () => (trimmed ? QUESTIONS.map((q) => q.replace("{k}", trimmed.toLowerCase())) : []),
    [trimmed],
  );

  const visible = filter === "All" ? ideas : ideas.filter((i) => i.intent === filter);

  const copyAll = async () => {
    await navigator.clipboard.writeText(ideas.map((i) => i.keyword).join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // Clear stale live results whenever the seed or country changes.
  useEffect(() => {
    setReal(null);
    setRealError(null);
  }, [trimmed, country]);

  const loadReal = async () => {
    if (!trimmed) return;
    setRealLoading(true);
    setRealError(null);
    try {
      const result = await fetchReal({ data: { seed: trimmed, country } });
      setReal(result.suggestions);
      if (result.suggestions.length === 0) {
        setRealError("Google returned no suggestions for this term. Try a broader seed keyword.");
      }
    } catch {
      setRealError("Could not reach Google right now. Please try again in a moment.");
    } finally {
      setRealLoading(false);
    }
  };


  return (
    <>
      <SiteNav />
      <main className="bg-white">
        <section className="border-b border-brand-deep/5 bg-brand-muted/40 py-16">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-brand-accent">
              Free Tool
            </p>
            <h1 className="text-4xl font-bold tracking-tight text-brand-deep md:text-5xl">
              Free Long Tail Keyword Generator
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-brand-deep/70">
              Enter one seed keyword and get long-tail variations, question keywords and semantic
              terms — grouped by search intent with a competition estimate for each.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto max-w-4xl px-6">
            <label htmlFor="seed-input" className="mb-2 block text-sm font-semibold text-brand-deep">
              Your seed keyword
            </label>
            <input
              id="seed-input"
              type="text"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder="e.g. content marketing agency"
              className="w-full rounded-xl border border-brand-deep/15 bg-white px-4 py-3 text-lg text-brand-deep shadow-sm outline-none focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20"
            />

            {!trimmed && (
              <div className="mt-6 flex flex-wrap items-center gap-2 text-sm text-brand-deep/60">
                <span>Try:</span>
                {["seo services", "content marketing", "social media optimization"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSeed(s)}
                    className="rounded-full border border-brand-deep/10 px-3 py-1 text-xs font-medium text-brand-deep transition hover:border-brand-accent hover:text-brand-accent"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {trimmed && (
              <div className="mt-10 space-y-10">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {(["All", "Informational", "Commercial", "Transactional", "Local / niche"] as const).map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setFilter(f)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                          filter === f
                            ? "bg-brand-accent text-white"
                            : "border border-brand-deep/10 text-brand-deep/70 hover:border-brand-accent hover:text-brand-accent"
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => void copyAll()}
                    className="rounded-full border border-brand-deep/15 px-4 py-1.5 text-xs font-semibold text-brand-deep transition hover:border-brand-accent hover:text-brand-accent"
                  >
                    {copied ? "Copied ✓" : `Copy all ${ideas.length} keywords`}
                  </button>
                </div>

                <p className="-mt-6 text-xs leading-relaxed text-brand-deep/55">
                  The keyword ideas below are real search patterns built from your seed term. The
                  competition estimate is based on how specific each phrase is — it is not live
                  Google search-volume data. Confirm volumes in Google Keyword Planner before you
                  commit to a term.
                </p>

                <div className="overflow-hidden rounded-2xl border border-brand-deep/10">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-brand-muted/40 text-[11px] uppercase tracking-widest text-brand-deep/60">
                      <tr>
                        <th scope="col" className="px-4 py-3 font-bold">Keyword</th>
                        <th scope="col" className="hidden px-4 py-3 font-bold sm:table-cell">Intent</th>
                        <th scope="col" className="px-4 py-3 font-bold">Est. competition</th>
                        <th scope="col" className="px-4 py-3 font-bold">Opportunity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-deep/5">
                      {visible.map((i) => (
                        <tr key={i.keyword} className="transition hover:bg-brand-muted/20">
                          <td className="px-4 py-3 font-medium text-brand-deep">
                            {i.keyword}
                            <span className="ml-2 text-xs text-brand-deep/45">{i.type}</span>
                          </td>
                          <td className="hidden px-4 py-3 sm:table-cell">
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${INTENT_STYLES[i.intent]}`}>
                              {i.intent}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-brand-deep/10">
                                <div
                                  className={`h-full rounded-full ${i.difficulty < 30 ? "bg-emerald-500" : i.difficulty < 55 ? "bg-amber-500" : "bg-rose-500"}`}
                                  style={{ width: `${i.difficulty}%` }}
                                />
                              </div>
                              <span className="tabular-nums text-xs text-brand-deep/70">{i.difficulty}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`text-xs font-semibold ${
                                i.opportunity === "High"
                                  ? "text-emerald-600"
                                  : i.opportunity === "Medium"
                                    ? "text-amber-600"
                                    : "text-rose-600"
                              }`}
                            >
                              {i.opportunity}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-2xl border-2 border-brand-accent/25 bg-brand-muted/25 p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">
                        Live Google data
                      </p>
                      <h2 className="mt-1 text-lg font-bold text-brand-deep">
                        What people actually search on Google
                      </h2>
                      <p className="mt-1 text-sm text-brand-deep/70">
                        Real autocomplete queries pulled live from Google for “{trimmed}”, in Google’s
                        own relevance order.
                      </p>
                    </div>
                    {user && (
                      <div className="flex flex-wrap items-center gap-2">
                        <label htmlFor="country-select" className="sr-only">
                          Country
                        </label>
                        <select
                          id="country-select"
                          value={country}
                          onChange={(e) => setCountry(e.target.value as typeof country)}
                          className="rounded-full border border-brand-deep/15 bg-white px-3 py-2 text-xs font-semibold text-brand-deep"
                        >
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => void loadReal()}
                          disabled={realLoading}
                          className="rounded-full bg-brand-accent px-5 py-2 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
                        >
                          {realLoading ? "Fetching…" : real ? "Refresh" : "Get live results"}
                        </button>
                      </div>
                    )}
                  </div>

                  {!user && !sessionLoading && (
                    <div className="mt-5 rounded-xl border border-brand-deep/10 bg-white p-5 text-center">
                      <p className="text-sm font-semibold text-brand-deep">
                        Free account required for live Google results
                      </p>
                      <p className="mx-auto mt-1 max-w-md text-sm text-brand-deep/65">
                        The keyword ideas above are free for everyone. Create a free account to pull
                        real Google autocomplete queries by country.
                      </p>
                      <Link
                        to="/auth"
                        search={{ next: "/tools/keyword-explorer" }}
                        className="mt-4 inline-block rounded-full bg-brand-deep px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                      >
                        Create a free account
                      </Link>
                    </div>
                  )}

                  {realError && (
                    <p className="mt-4 text-sm text-rose-600">{realError}</p>
                  )}

                  {user && real && real.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {real.map((s) => (
                        <span
                          key={s.keyword}
                          className={`rounded-full px-3 py-1.5 text-xs font-medium ${GROUP_STYLES[s.group]}`}
                        >
                          {s.keyword}
                        </span>
                      ))}
                    </div>
                  )}

                  {user && real && real.length > 0 && (
                    <p className="mt-4 text-xs leading-relaxed text-brand-deep/55">
                      Source: Google autocomplete, fetched live. These are genuine searcher queries.
                      Google does not publish search volume here, so no volume figures are shown —
                      check volumes in Google Keyword Planner.
                    </p>
                  )}
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="rounded-2xl border border-brand-deep/10 p-6">
                    <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-brand-deep">
                      Question keywords
                    </h2>
                    <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-brand-deep/75">
                      {questions.map((q) => (
                        <li key={q}>{q}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-brand-deep/10 p-6">
                    <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-brand-deep">
                      Semantic terms to include
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {terms.map((t) => (
                        <span key={t} className="rounded-full bg-brand-muted/60 px-3 py-1 text-xs text-brand-deep/75">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <section className="mt-16">
              <h2 className="text-2xl font-bold text-brand-deep">Keyword research FAQs</h2>
              <div className="mt-6 space-y-4">
                {FAQS.map((f) => (
                  <details key={f.q} className="rounded-2xl border border-brand-deep/10 p-5">
                    <summary className="cursor-pointer text-sm font-semibold text-brand-deep">{f.q}</summary>
                    <p className="mt-3 text-sm leading-relaxed text-brand-deep/75">
                      <FaqAnswer text={f.a} />
                    </p>
                  </details>
                ))}
              </div>
            </section>

            <div className="mt-14 rounded-2xl bg-brand-deep p-8 text-center">
              <h2 className="text-2xl font-bold text-white">Want a full keyword map built for you?</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-white/70">
                We research, cluster, and map keywords to pages — then write and optimise the content
                that ranks for them.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Link
                  to="/services"
                  className="rounded-full bg-brand-accent px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  Explore Our Services
                </Link>
                <Link
                  to="/tools/headline-analyzer"
                  className="rounded-full border border-white/30 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  Try Headline Analyzer
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function FaqAnswer({ text }: { text: string }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return (
    <>
      {parts.map((part, idx) => {
        const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (!m) return <span key={idx}>{part}</span>;
        return (
          <Link key={idx} to={m[2]} className="font-semibold text-brand-accent hover:underline">
            {m[1]}
          </Link>
        );
      })}
    </>
  );
}
